import type {
  GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePartnerAdminsUsersUserId200,
} from '@fukuri-admins/apis/client/gen/model';

type Args = {
  /** パートナー機能フラグ一覧のレスポンス（useGetPartnerAdminsPartnerFeatureFlags の data） */
  partnerFeatureFlagData: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  /** アプリケーション設定のレスポンス（useConfiguration() の applicationConfiguration） */
  applicationConfiguration: GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200;
  /** 編集対象利用者データのレスポンス（useGetPartnerAdminsUser の data） */
  data: GetApiV1ThreeLetterCodePartnerAdminsUsersUserId200;
};

type IResolveFieldRequirements = (args: Args) => {
  /** メールアドレスが必須かどうか */
  isEmailRequired: boolean;
  /** 電話番号フィールドを表示するかどうか */
  isPhoneNumberFieldVisible: boolean;
  /** メールアドレス・電話番号のどちらか一方の入力を必須とするか（両方空を不可にするか） */
  requireEitherEmailOrPhoneNumber: boolean;
};

/**
 * activation_code フラグ・login_method・is_activated の組み合わせから、
 * 利用者編集フォームのメールアドレス・電話番号フィールドの必須/表示制御ルールを導出する
 *
 * activation_code PartnerFeatureFlag が有効かどうか（partnerFeatureFlagData）・
 * テナントの初回ログイン受け付け方式（applicationConfiguration）・
 * 編集対象利用者のアクティベーション済みフラグ（data）の取り出しは、
 * この関数内に閉じ込める（呼び出し側の hook は生データをそのまま渡すだけでよい）。
 *
 * ルール:
 * - activation_code が無効 → メールアドレス必須（電話番号フィールドは非表示）
 * - activation_code が有効 かつ login_method が email → メールアドレス必須（電話番号フィールドは非表示）
 * - activation_code が有効 かつ login_method が phone_number → 電話番号フィールドを表示する
 *   - is_activated が false → メールアドレス・電話番号とも任意（両方空も許容）
 *   - is_activated が true → メールアドレス・電話番号のどちらか一方の入力を必須とする
 *
 * @param args.partnerFeatureFlagData - パートナー機能フラグ一覧のレスポンス
 * @param args.applicationConfiguration - アプリケーション設定のレスポンス
 * @param args.data - 編集対象利用者データのレスポンス
 */
export const resolveFieldRequirements: IResolveFieldRequirements = ({
  partnerFeatureFlagData,
  applicationConfiguration,
  data,
}) => {
  const activationCodeEnabled = partnerFeatureFlagData.partner_feature_flags.some(
    flag => flag.feature_key === 'activation_code' && flag.enabled
  );
  const loginMethod = applicationConfiguration.application_configuration.login_method;
  const isActivated = data.user.is_activated;

  const isPhoneNumberFieldVisible = activationCodeEnabled && loginMethod === 'phone_number';

  if (!isPhoneNumberFieldVisible) {
    return {
      isEmailRequired: true,
      isPhoneNumberFieldVisible: false,
      requireEitherEmailOrPhoneNumber: false,
    };
  }

  return {
    isEmailRequired: false,
    isPhoneNumberFieldVisible: true,
    requireEitherEmailOrPhoneNumber: isActivated,
  };
};
